// Chess — SFX module.
//
// Shares the same Web-Audio primitives as Rummy / Ludo so the tap /
// slot-lock / chime timbres match across games. No real chess-specific
// sounds yet (move clack, check alert, checkmate fanfare) — those
// come with the engine pass.

let ctx: AudioContext | null = null;
let sfxEnabled = true;

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  const Cls = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Cls) return null;
  ctx = new Cls();
  return ctx;
}

export function primeAudio() {
  const c = ensureCtx(); if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
}

export function setSfxEnabled(on: boolean) { sfxEnabled = !!on; }

// Tactile UI tap — bet tile, player-count toggle, settings row.
export function playTap() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'square';
  const t = c.currentTime;
  o.frequency.setValueAtTime(1200, t);
  g.gain.setValueAtTime(0.06, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
  o.connect(g).connect(c.destination);
  o.start(t); o.stop(t + 0.06);
}

// Three-note slot-lock chord used when a player slot fills.
export function playSlotLock() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t0 = c.currentTime;
  const notes = [
    { f: 523.25, dt: 0.00 },
    { f: 659.25, dt: 0.06 },
    { f: 880.00, dt: 0.12 },
  ];
  for (const n of notes) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sine';
    const ts = t0 + n.dt;
    o.frequency.setValueAtTime(n.f, ts);
    g.gain.setValueAtTime(0.0001, ts);
    g.gain.exponentialRampToValueAtTime(0.12, ts + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, ts + 0.14);
    o.connect(g).connect(c.destination);
    o.start(ts); o.stop(ts + 0.16);
  }
}

// ─── Chess-specific SFX ─────────────────────────────────────────────

// Band-limited noise burst — building block for wood clacks.
function noise(vol: number, band: number, duration: number) {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t = c.currentTime;
  const size = Math.floor(c.sampleRate * duration);
  const buf = c.createBuffer(1, size, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < size; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource(); src.buffer = buf;
  const filt = c.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = band;
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);
  src.connect(filt); filt.connect(g); g.connect(c.destination);
  src.start(t);
}

function tone(freq: number, dur: number, vol = 0.2, type: OscillatorType = 'sine', slide = 0) {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slide !== 0) o.frequency.linearRampToValueAtTime(freq + slide, t + dur);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.015);
  g.gain.linearRampToValueAtTime(0, t + dur);
  o.connect(g); g.connect(c.destination);
  o.start(t); o.stop(t + dur);
}

/** Quiet wood clack when a piece settles on a square. */
export function playMove() {
  noise(0.45, 1800, 0.06);
}

/** Brighter clack + low thud when a capture happens. */
export function playCapture() {
  noise(0.55, 2400, 0.07);
  setTimeout(() => noise(0.4, 300, 0.1), 40);
}

/** Two-stage clack for castling (king + rook land nearly together). */
export function playCastle() {
  noise(0.4, 1800, 0.05);
  setTimeout(() => noise(0.4, 1800, 0.05), 90);
}

/** Sharp high tri-tone when OUR king is in check. */
export function playCheck() {
  tone(1200, 0.08, 0.25, 'square');
  setTimeout(() => tone(900, 0.08, 0.2, 'square'), 80);
}

/** 4-note rising fanfare on checkmate win. */
export function playCheckmate() {
  const notes = [523.25, 659.25, 783.99, 1046.5];    // C5 E5 G5 C6
  notes.forEach((f, i) => setTimeout(() => tone(f, 0.32, 0.28, 'sine'), i * 120));
}

/** Descending 4-note loss tone (C Bb G Eb) on match loss. */
export function playDefeat() {
  const notes = [523.25, 466.16, 392.0, 311.13];
  notes.forEach((f, i) => setTimeout(() => tone(f, 0.3, 0.22, 'sine'), i * 150));
}

/** Neutral two-note chime on any draw result. */
export function playDraw() {
  tone(523.25, 0.25, 0.2, 'sine');
  setTimeout(() => tone(440, 0.3, 0.2, 'sine'), 140);
}

/** Ascending sparkle when a pawn successfully promotes. */
export function playPromote() {
  [783.99, 987.77, 1318.5].forEach((f, i) =>
    setTimeout(() => tone(f, 0.18, 0.18, 'sine'), i * 70));
}

/** Short clock-tick beep. Played by the shared clock-warning
 *  scheduler (startClockWarning below) at a cadence that scales
 *  with the remaining time — 1/s under 60s, 2/s under 30s, and
 *  a tight 10/s burst for the last 5s. */
export function playClockTick() {
  tone(1100, 0.05, 0.18, 'square');
}

let _clockWarnTimer: number | null = null;
let _clockWarnPhase: 'none' | '60' | '30' | '5' = 'none';

/**
 * Public API: the client's clock-watcher (in Page.tsx) calls this
 * every time it ticks, passing the remaining milliseconds on the
 * CURRENTLY-ACTIVE side's clock. This module keeps a single timer
 * running that matches the threshold we're in. Switching thresholds
 * (e.g., crossing 30 s) rearms the timer at the new cadence; going
 * back above 60 s (e.g., on an increment that pushes us over) stops
 * the ticking. Also stops when a match ends or the game isn't live.
 *
 * Both players see the same clock values, so both browsers fire the
 * same sounds — the user spec "both users will be able to hear the
 * sound of each other" resolves naturally: whichever side's clock is
 * running, both sides hear the warning.
 */
export function updateClockWarning(remainingMs: number, active: boolean, leadSec: number = 60) {
  if (!sfxEnabled || !active || remainingMs <= 0) {
    stopClockWarning();
    return;
  }
  // Lead time is admin-controlled (Chess URGENCY_BEEP_LEAD_SECONDS).
  // 0 disables the entire ramp; otherwise the FIRST cadence step
  // starts at this many seconds before clock-zero. The 30/5-second
  // sub-thresholds remain fixed since they encode the "panic ramp"
  // shape — only the entry point is admin-tunable.
  if (leadSec <= 0) { stopClockWarning(); return; }
  const leadMs = leadSec * 1000;
  let phase: 'none' | '60' | '30' | '5' = 'none';
  if (remainingMs <= 5_000)   phase = '5';
  else if (remainingMs <= 30_000) phase = '30';
  else if (remainingMs <= leadMs) phase = '60';

  if (phase === _clockWarnPhase) return;
  stopClockWarning();
  _clockWarnPhase = phase;
  if (phase === 'none') return;

  // Cadence per user spec:
  //   60-30s → one beep per second
  //   30-5s  → two beeps per second (500ms apart)
  //    <5s   → continuous/fast beeps (every 100ms)
  const interval = phase === '5' ? 100 : phase === '30' ? 500 : 1000;
  playClockTick();
  _clockWarnTimer = window.setInterval(() => playClockTick(), interval);
}

export function stopClockWarning() {
  if (_clockWarnTimer != null) {
    window.clearInterval(_clockWarnTimer);
    _clockWarnTimer = null;
  }
  _clockWarnPhase = 'none';
}

/** Warm bell chime when OUR turn begins — timbrally distinct from
 *  every other SFX so the player recognises it instantly. */
export function playYourTurnChime() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t0 = c.currentTime;
  const partials = [
    { f: 523.25, gain: 0.11 },    // C5
    { f: 783.99, gain: 0.09 },    // G5 (perfect fifth)
    { f: 1568.0, gain: 0.03 },    // G6 sparkle
  ];
  for (const p of partials) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(p.f, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(p.gain, t0 + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
    o.connect(g).connect(c.destination);
    o.start(t0); o.stop(t0 + 0.65);
  }
}
